import React, { useContext } from "react";
import styled from "styled-components";
import Image from "gatsby-image";
import DomeHeader from "../components/DomeHeader";
import { Link } from "gatsby";
import RatingStars from "../components/RatingStars";
import Breakpoints from "../components/styles/Breakpoints";
import { Colors } from "../components/styles/Colors";
import KaramoProduct from "../components/KaramoProduct";
import {
  DomeText,
  AcmeCompressed,
  Header3,
  Header4,
  ProximaNova,
} from "../components/typography";
import { TertiaryButton } from "../components/Button";
import richText from "../utils/richText";
import { addSubscriptionItem } from "../utils/StoreActions";
import { store } from "../utils/StoreContext";

const Wrapper = styled.div`
  background: ${Colors.cream1};

  .title {
    padding: 44px 30px;

    @media ${Breakpoints.desktop} {
      padding: 64px 32px;
    }

    h2 {
      margin: 0px auto 40px;
      max-width: 800px;
      font-family: ${AcmeCompressed};
      line-height: 1;

      color: ${Colors.hunter};
      text-align: center;
      font-size: 64px;

      @media ${Breakpoints.desktop} {
        margin: 24px auto 80px;
        font-size: 120px;
      }
    }

    > div:last-child div:first-child {
      ${DomeText} {
        text-align: center;
        display: block;
        color: ${Colors.greyLight};
      }
    }

    @media ${Breakpoints.tablet} {
      > div:last-child div:first-child {
        ${DomeText} {
          text-align: left;
        }
      }
    }
  }
`;

const FeaturedProduct = styled.div`
  display: flex;
  flex-wrap: wrap;

  .left,
  .right {
    width: 100%;

    @media ${Breakpoints.desktop} {
      width: 50%;
    }
  }

  .left {
    background-color: ${Colors.sage};
    padding: 50px 30px;
    order: 2;

    @media ${Breakpoints.desktop} {
      order: 1;
      padding: 130px;
    }

    ${Header3} {
      color: ${Colors.lightBlue};
      opacity: 0.8;
      margin-bottom: 20px;
    }

    .text {
      color: ${Colors.lightBlue};
      opacity: 0.8;
      position: relative;
      margin-bottom: 30px;

      p {
        display: inline;
      }

      span:first-child {
        @media ${Breakpoints.desktop} {
          display: none;
        }
      }

      &:before {
        content: none;
        color: ${Colors.lightBlue};
        font-size: 64px;
        position: absolute;
        top: 0px;
        left: 0px;
        font-family: ${ProximaNova};
        font-weight: 300;
        line-height: 1;
        transform: translate(-110%, -20%);
        opacity: 0.7;

        @media ${Breakpoints.desktop} {
          content: "“";
        }
      }
    }

    .buttons {
      display: block;

      @media ${Breakpoints.desktop} {
        display: flex;
      }

      & > ${TertiaryButton}:first-child {
        border: 0;
        width: 100%;
        margin-bottom: 15px;

        @media ${Breakpoints.desktop} {
          margin-right: 20px;
          margin-bottom: 0px;
          width: auto;
        }
      }

      & > ${TertiaryButton}:last-child {
        border: 1px solid ${Colors.cream3};
        color: ${Colors.cream3};
        background-color: transparent;
        transition: color 0.3s, background-color 0.3s;
        width: 100%;

        &:hover {
          background-color: ${Colors.cream3};
          color: ${Colors.hunter};
        }

        @media ${Breakpoints.desktop} {
          width: auto;
        }
      }
    }
  }

  .right {
    position: relative;
    order: 1;
    height: 30vh;

    @media ${Breakpoints.desktop} {
      order: 2;
      height: auto;
    }

    .gatsby-image-wrapper {
      top: 0px;
      left: 0px;
      width: 100%;
      height: 100%;
    }
  }
`;

const OtherProducts = styled.div`
  padding: 40px 30px;

  @media ${Breakpoints.desktop} {
    padding: 100px 130px;
    display: flex;
  }

  .left {
    @media ${Breakpoints.desktop} {
      max-width: 250px;
    }

    h2 {
      font-family: ${AcmeCompressed};
      font-size: 72px;
      line-height: 1;
      color: ${Colors.hunter};
      text-align: center;

      @media ${Breakpoints.desktop} {
        text-align: left;
      }
    }
  }

  .right {
    width: 100%;

    @media ${Breakpoints.desktop} {
      padding-left: 100px;
    }
  }
`;

export default function KaramoProducts({ featuredProduct, otherProducts }) {
  let count = null;
  let rating = null;
  const { dispatch } = useContext(store);

  if (featuredProduct.product.__typename === "ContentfulProductSet") {
    const ratingCounts = featuredProduct.product.variants.map(
      (prod) => prod.product?.rating.totalReviews
    );
    count = ratingCounts.reduce((a, b) => a + b);

    const ratingScores = featuredProduct.product.variants.map(
      (prod) => prod.product?.rating.score
    );
    rating = ratingScores.reduce((a, b) => a + b) / ratingScores.length;
  } else {
    count = featuredProduct.product?.rating.totalReviews;
    rating = featuredProduct.product?.rating.score;
  }

  function subscribe() {
    let item = {
      name: featuredProduct.product.name,
      selectedVariant: featuredProduct.product.variants[0],
    };

    dispatch(addSubscriptionItem(item, 1, null, true));
  }

  return (
    <Wrapper id="favorites">
      <div className="title">
        <DomeHeader text="What I'm Using The Most" centered />
        <h2>My Favorites</h2>
      </div>

      <FeaturedProduct>
        <div className="left">
          <Header3>{featuredProduct.product.name}</Header3>
          <RatingStars
            count={count}
            score={rating}
            fill={Colors.lightBlue}
            textColor={Colors.lightBlue}
          />
          <div className="text">
            <span>“</span>
            {richText(featuredProduct.description.raw)}
            <span>”</span>
          </div>
          <div className="buttons">
            <TertiaryButton onClick={() => subscribe()}>
              Subscribe
            </TertiaryButton>

            <TertiaryButton
              as={Link}
              to={`/products/${featuredProduct.product.slug}`}
            >
              View More
            </TertiaryButton>
          </div>
        </div>

        <div className="right">
          <Image
            style={{ position: "absolute" }}
            fluid={featuredProduct.image.fluid}
          />
        </div>
      </FeaturedProduct>

      <OtherProducts>
        <div className="left">
          <h2>But If I Had To Choose:</h2>
        </div>

        <div className="right">
          {otherProducts.map((product, index) => (
            <KaramoProduct key={index} product={product}></KaramoProduct>
          ))}
        </div>
      </OtherProducts>
    </Wrapper>
  );
}
